import {
  Card,
  Container,
  Group,
  Menu,
  rem,
  ScrollArea,
  Space,
  Tabs,
  TabsValue,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback} from "react";
import {ServiceEventEntity} from "@ots-frontend/ots-data";
import {ServiceEventDetails} from "./ServiceEventDetails";
import {createContentStyles} from "../../../theme/useContentStyles";
import {IconChevronDown, IconMap} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {ServiceEventActionMenuDropdown} from "../../menu/ServiceEventActionMenu/ServiceEventActionMenu";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";
import {Badge} from "../../badge/Badge/Badge";
import {ServiceEventLocationFacetMasterDetail} from "./ServiceEventLocationFacetMasterDetail";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type ServiceEventProfileProps = PropsWithChildren<{
  serviceEvent: ServiceEventEntity
}>

const tabs = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: "locations",
    value: "Locations"
  },
  {
    key: "map",
    value: "Map",
    icon: <IconMap size={14}/>
  },
  {
    key: "history",
    value: "Change Logs",
    permissions: ["change_log_view"]
  },
];

export const ServiceEventProfile = (props: ServiceEventProfileProps) => {
  const {serviceEvent} = props;

  const {classes} = useStyles();

  const {t} = useTranslation()
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key} icon={tab.icon}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  const onTabChange = useCallback((value: TabsValue) => {
    if (value === 'map') {
      navigate(`map?id=${serviceEvent.id}`)
    } else {
      navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})
    }
  }, [navigate, pathname, search, state, serviceEvent])

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>
            Service Event: {serviceEvent.name}
          </Title>

          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <ServiceEventActionMenuDropdown serviceEvent={serviceEvent}/>
            </Menu.Dropdown>
          </Menu>

        </Group>

        <Group spacing="xs" mt="xs">
          <Badge variant="filled" color="blue">
            <FormattedDatabaseCode value={serviceEvent.eventType}/>
          </Badge>

          {serviceEvent.isActive() ?
            <Badge color="green">{t("common.status.active", "Active")}</Badge>
            :
            <Badge color="gray">{t("common.status.inactive", "Inactive")}</Badge>
          }
        </Group>

      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={onTabChange}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <ServiceEventDetails serviceEvent={serviceEvent}/>
            </Tabs.Panel>
            <Tabs.Panel value="locations">
              <ScrollArea mih="65vh" type="auto">
                <ServiceEventLocationFacetMasterDetail serviceEvent={serviceEvent}/>
              </ScrollArea>
            </Tabs.Panel>
            <Tabs.Panel value="map">
              <></>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={serviceEvent}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
